<template>
  <div class="space-editor">
    <input 
      type="file" 
      ref="fileInput" 
      accept="image/*" 
      class="hidden-input" 
      @change="handleFileSelection"
      key="fileInput"
    >
    
    <!-- Controls at the top -->
    <div class="controls-top">
      <v-btn color="error" @click.stop="clearCanvas">Clear</v-btn>
      <v-btn color="primary" @click.stop="triggerFileInput">
        <v-icon left>mdi-file-image-plus</v-icon>
        Change Layout
      </v-btn>
    </div>
    
    <div class="canvas-container">
      <div class="canvas-wrapper">
        <div class="canvas-area" :style="canvasAreaStyle">
          <!-- File selection prompt overlay when no layout image -->
          <div v-if="!localLayout" class="file-selection-prompt" @click.stop="triggerFileInput">
            <v-icon large color="primary">mdi-file-image-plus</v-icon>
            <p>Click here to select a layout image</p>
          </div>
          
          <!-- Draggable icon -->
          <div 
            v-if="localLayout && $parent.currentIcon" 
            class="draggable-icon" 
            :style="[iconPositionStyle, equipmentIconStyle]"
            @mousedown="startDraggingIcon"
            @touchstart="startDraggingIcon"
          >
            <v-icon :color="currentIconColor" size="24">{{ currentIconType }}</v-icon>
          </div>
          
          <!-- Placed asset icons -->
          <div v-if="localLayout">
            <div 
              v-for="asset in placedAssets" 
              :key="asset.uuid"
              class="placed-asset-icon"
              :class="{
                'highlighted': isAssetHighlighted(asset),
                'dragging': isDraggingPlacedAsset && currentDraggedAsset && currentDraggedAsset.uuid === asset.uuid
              }"
              :style="[getPlacedAssetStyle(asset), assetIconStyle]"
              @mousedown="startDraggingPlacedAsset($event, asset)"
              @touchstart="startDraggingPlacedAsset($event, asset)"
            >
              <v-icon :color="getAssetColor(asset)" :size="isAssetHighlighted(asset) ? 36 : 24">
                {{ getAssetIcon(asset) }}
              </v-icon>
              <div class="asset-label">{{ asset.assetName }}</div>
            </div>
          </div>
          
          <canvas 
            ref="drawCanvas" 
            class="draw-canvas"
            :width="width"
            :height="effectiveHeight"
            @mousedown="handleCanvasMouseDown"
            @mousemove="handleCanvasMouseMove"
            @mouseup="handleCanvasMouseUp"
            @mouseleave="handleCanvasMouseLeave"
            @touchstart="handleTouchStart"
            @touchmove="handleTouchMove"
            @touchend="handleTouchEnd"
            @touchcancel="handleTouchEnd"
          ></canvas>
        </div>
      </div>
    </div>
    
    <!-- Space Name Dialog -->
    <v-dialog v-model="showNameDialog" max-width="400px" persistent>
      <v-card>
        <v-card-title>Name Your Space</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newSpaceName"
            label="Space Name"
            required
            autofocus
            @keyup.enter="confirmSpaceName"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="cancelDrawing">Cancel</v-btn>
          <v-btn color="blue darken-1" text @click="confirmSpaceName">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ShortUniqueId from 'short-unique-id';

export default {
  name: 'SpaceEditor',
  props: {
    layout: {
      type: String,
      default: null
    },
    width: {
      type: Number,
      default: 500
    },
    height: {
      type: Number,
      default: 400
    },
    lineWidth: {
      type: Number,
      default: 2
    },
    placedAssets: {
      type: Array,
      default: () => []
    },
    hoveredSpace: {
      type: Object,
      default: null
    },
    hoveredAsset: {
      type: Object,
      default: null
    },
    getAssetIcon: {
      type: Function,
      default: (asset) => asset?.type === 'camera' ? 'mdi-camera' : 'mdi-camera'
    },
    getAssetColor: {
      type: Function,
      default: (asset) => asset?.type === 'camera' ? 'blue' : 'grey'
    },
    spaces: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isDrawing: false,
      isDrawingMode: true,
      drawContext: null,
      coordinates: [],
      currentPath: [],
      internalSpaces: [], // Array to store locally created spaces
      drawImage: null,
      currentLayoutSrc: null,
      // Fixed light blue color with 50% opacity
      // drawingColor: { rgba: { r: 135, g: 206, b: 250, a: 0.5 } },
      drawingColor: null,
      isLoading: false,
      // Local copy of the layout prop
      localLayout: null,
      // Space naming dialog
      showNameDialog: false,
      newSpaceName: "",
      pendingSpace: null,
      // Draggable icon
      iconPosition: { x: 50, y: 50 },
      isDraggingIcon: false,
      iconInSpace: null,
      // Track multiple spaces when overlapping
      iconInSpaces: [],
      // For dragging placed assets
      isDraggingPlacedAsset: false,
      currentDraggedAsset: null,
      tempDragPosition: null, // Temporary position during dragging
      // Flag to track if we should disable asset pointer events
      disableAssetInteraction: false
    }
  },
  created() {
    const successColor = this.getThemeColorWithOpacity('success', 0.5);

    // Parse the rgba components
    const rgbaMatch = successColor.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
    if (rgbaMatch) {
      this.drawingColor = {
        rgba: {
          r: parseInt(rgbaMatch[1]),
          g: parseInt(rgbaMatch[2]),
          b: parseInt(rgbaMatch[3]),
          a: parseFloat(rgbaMatch[4])
        }
      };
    } else {
      // Fallback to light blue if parsing fails
      this.drawingColor = { rgba: { r: 76, g: 175, b: 80, a: 0.5 } };
    }
  },
  computed: {
    effectiveHeight() {
      // If no layout is selected, use a small default height
      if (!this.localLayout) {
        return Math.min(200, this.height);
      }
      
      // If layout is selected, use the provided height but cap at 1000px
      return Math.min(1000, this.height);
    },
    allSpaces() {
      // Combine prop spaces and internal spaces
      return [...this.spaces, ...this.internalSpaces];
    },
    canvasAreaStyle() {
      return {
        height: `${this.effectiveHeight}px`
      };
    },
    iconPositionStyle() {
      return {
        left: `${this.iconPosition.x}px`,
        top: `${this.iconPosition.y}px`
      };
    },
    currentIconType() {
      // Get the icon type from the parent component's currentIcon
      const currentIcon = this.$parent.currentIcon;
      if (!currentIcon) return '';
      
      return this.getAssetIcon(currentIcon);
    },
    currentIconColor() {
      // Get the icon color from the parent component's currentIcon
      const currentIcon = this.$parent.currentIcon;
      if (!currentIcon) return '';
      
      return this.getAssetColor(currentIcon);
    },
    assetIconStyle() {
      return {
        pointerEvents: this.disableAssetInteraction ? 'none' : 'auto'
      };
    }
  },
  watch: {
    lineWidth() {
      this.$nextTick(() => {
        this.updateDrawingStyles();
      });
    },
    layout: {
      handler(newVal) {
        // console.log('===== LAYOUT PROP WATCHER TRIGGERED =====');
        // console.log('Layout prop changed to:', newVal);
        // console.log('Current localLayout:', this.localLayout);
        // console.log('Current currentLayoutSrc:', this.currentLayoutSrc);
        
        // Update local layout when prop changes
        this.localLayout = newVal;
        // console.log('Updated localLayout to:', this.localLayout);
        
        if (newVal !== this.currentLayoutSrc) {
          // console.log('Layout source changed, updating currentLayoutSrc and loading image');
          this.currentLayoutSrc = newVal;
          this.$nextTick(() => {
            // console.log('Next tick reached, loading layout image');
            this.loadLayoutImage();
            this.redrawCanvas();
          });
        } else {
          // console.log('Layout source is the same as currentLayoutSrc, not loading new image');
        }
        // console.log('===== LAYOUT PROP WATCHER COMPLETED =====');
      },
      immediate: true
    },
    localLayout(newVal) {
      // When local layout changes, emit event to update parent
      if (newVal !== this.layout) {
        this.$emit('update:layout', newVal);
      }
      
      // Clear spaces when layout changes
      if (newVal !== this.currentLayoutSrc) {
        this.internalSpaces = [];
        this.$emit('spaces-updated', this.internalSpaces);
      }
    },
    width: {
      handler(newVal) {
        // console.log('Width changed to:', newVal);
        this.$nextTick(() => {
          this.updateCanvasDimensions();
        });
      },
      immediate: true
    },
    height: {
      handler(newVal) {
        // console.log('Height changed to:', newVal);
        this.$nextTick(() => {
          this.updateCanvasDimensions();
        });
      },
      immediate: true
    },
    iconInSpaces(newVal, oldVal) {
      // Compare arrays by stringifying them
      const newValStr = JSON.stringify(newVal.map(s => s.uuid).sort());
      const oldValStr = JSON.stringify((oldVal || []).map(s => s.uuid).sort());
      
      if (newValStr !== oldValStr) {
        // Emit event when icon moves to different spaces
        this.$emit('icon-in-spaces', newVal);
        
        // For backward compatibility, also update iconInSpace with the first space
        this.iconInSpace = newVal.length > 0 ? newVal[0] : null;
      }
    },
    iconInSpace(newVal, oldVal) {
      if (newVal !== oldVal) {
        // Emit event when icon moves to a different space (for backward compatibility)
        this.$emit('icon-in-space', newVal);
      }
    },
    hoveredSpace(newVal) {
      // Redraw canvas when hovered space changes
      this.redrawCanvas();
    },
    hoveredAsset(newVal) {
      // No need to redraw canvas for assets as we use CSS for that
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initializeCanvases();
      this.loadLayoutImage();
      
      // Add global event listeners for dragging
      window.addEventListener('mousemove', this.moveIcon);
      window.addEventListener('mouseup', this.stopDraggingIcon);
      window.addEventListener('touchmove', this.moveIconTouch);
      window.addEventListener('touchend', this.stopDraggingIcon);
    });
  },
  beforeDestroy() {
    // Remove global event listeners
    window.removeEventListener('mousemove', this.moveIcon);
    window.removeEventListener('mouseup', this.stopDraggingIcon);
    window.removeEventListener('touchmove', this.moveIconTouch);
    window.removeEventListener('touchend', this.stopDraggingIcon);
  },
  methods: {
    initializeCanvases() {
      this.updateCanvasDimensions();
    },
    // Add these utility methods for color handling
    hexToRgba(hex, opacity) {
      // Remove the hash if it exists
      hex = hex.replace('#', '');
      
      // Parse the hex values
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      
      // Return rgba string
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    },
    getThemeColorWithOpacity(colorName, opacity) {
      // Access the color from Vuetify theme
      const color = this.$vuetify.theme.themes[this.$vuetify.theme.dark ? 'dark' : 'light'][colorName];
      
      // Convert to rgba
      return this.hexToRgba(color, opacity);
    },
    updateDrawingStyles() {
      // Skip if drawing context is not available
      if (!this.drawContext) {
        // console.log('Drawing context not available yet, skipping style update');
        return;
      }
      
      // Calculate stroke color with full opacity
      const strokeRgba = this.drawingColor.rgba;
      const strokeColor = `rgba(${strokeRgba.r}, ${strokeRgba.g}, ${strokeRgba.b}, ${strokeRgba.a})`;
      
      // Calculate fill color with reduced opacity
      const fillOpacity = Math.max(0.1, strokeRgba.a / 2); // Half the opacity of stroke, but at least 0.1
      const fillColor = `rgba(${strokeRgba.r}, ${strokeRgba.g}, ${strokeRgba.b}, ${fillOpacity})`;
      
      // Update drawing context styles
      this.drawContext.lineJoin = 'round';
      this.drawContext.lineCap = 'round';
      this.drawContext.lineWidth = this.lineWidth;
      this.drawContext.strokeStyle = strokeColor;
      this.drawContext.fillStyle = fillColor;
      
      // Redraw canvas with new styles
      this.redrawCanvas();
    },
    // Icon dragging methods
    startDraggingIcon(event) {
      event.preventDefault();
      this.isDraggingIcon = true;
    },
    moveIcon(event) {
      if (this.isDraggingIcon) {
        const rect = this.$refs.drawCanvas.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        
        // Update icon position
        this.updateIconPosition(x, y);
      } else if (this.isDraggingPlacedAsset && this.currentDraggedAsset) {
        // Get mouse position relative to canvas
        const rect = this.$refs.drawCanvas.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        
        // Use requestAnimationFrame for smoother updates
        requestAnimationFrame(() => {
          this.updateTempDragPosition(x, y);
        });
      }
    },
    moveIconTouch(event) {
      if (!event.touches || !event.touches[0]) return;
      
      const touch = event.touches[0];
      const rect = this.$refs.drawCanvas.getBoundingClientRect();
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;
      
      if (this.isDraggingIcon) {
        // Update icon position
        this.updateIconPosition(x, y);
      } else if (this.isDraggingPlacedAsset && this.currentDraggedAsset) {
        // Use requestAnimationFrame for smoother updates
        requestAnimationFrame(() => {
          this.updateTempDragPosition(x, y);
        });
      }
    },
    updateIconPosition(x, y) {
      // Keep icon within canvas bounds
      const boundedX = Math.max(0, Math.min(this.width, x));
      const boundedY = Math.max(0, Math.min(this.effectiveHeight, y));
      
      this.iconPosition = { x: boundedX, y: boundedY };
      
      // Check which space the icon is in
      this.checkIconInSpace();
    },
    stopDraggingIcon() {
      if (this.isDraggingPlacedAsset && this.currentDraggedAsset) {
        // Now calculate the final position and spaces
        if (this.tempDragPosition) {
          this.finalizeAssetPosition(this.currentDraggedAsset, this.tempDragPosition.x, this.tempDragPosition.y);
        }
        
        // Reset dragging state
        this.isDraggingPlacedAsset = false;
        this.currentDraggedAsset = null;
        this.tempDragPosition = null;
      }
      
      this.isDraggingIcon = false;
    },
    checkIconInSpace(specificSpace = null) {
      // If no icon position, nothing to check
      if (!this.iconPosition) return;
      
      // Initialize arrays to track spaces
      let spacesContainingIcon = [];
      let spaceContainingIcon = null;
      
      // If a specific space is provided, only check that space
      if (specificSpace) {
        if (this.isPointInPath(this.iconPosition, specificSpace.path)) {
          spacesContainingIcon.push(specificSpace);
          spaceContainingIcon = specificSpace;
        }
      } else {
        // Check all spaces
        this.allSpaces.forEach(space => {
          if (this.isPointInPath(this.iconPosition, space.path)) {
            spacesContainingIcon.push(space);
            // Set the first space found as the primary space
            if (!spaceContainingIcon) {
              spaceContainingIcon = space;
            }
          }
        });
      }
      
      // Update state and emit events
      this.iconInSpace = spaceContainingIcon;
      this.iconInSpaces = spacesContainingIcon;
      
      this.$emit('icon-in-space', this.iconInSpace);
      this.$emit('icon-in-spaces', this.iconInSpaces);
    },
    isPointInPath(point, path) {
      // Create a temporary path to check if point is inside
      const ctx = this.drawContext;
      ctx.save();
      
      // Create a new path from the space path
      ctx.beginPath();
      ctx.moveTo(path[0].x, path[0].y);
      
      for (let i = 1; i < path.length; i++) {
        ctx.lineTo(path[i].x, path[i].y);
      }
      
      // Check if point is in path
      const isInside = ctx.isPointInPath(point.x, point.y);
      
      ctx.restore();
      return isInside;
    },
    // Canvas event handlers
    handleCanvasMouseDown(event) {
      // If icon is being dragged, don't start drawing
      if (this.isDraggingIcon) return;
      
      // If no layout is set, trigger file input
      if (!this.localLayout) {
        this.triggerFileInput(event);
        return;
      }
      
      // Otherwise, start drawing
      this.startDrawing(event);
    },
    handleCanvasMouseMove(event) {
      // If icon is being dragged, don't draw
      if (this.isDraggingIcon) return;
      
      this.draw(event);
    },
    handleCanvasMouseUp(event) {
      // If icon is being dragged, handle that separately
      if (this.isDraggingIcon) {
        this.stopDraggingIcon();
        return;
      }
      
      this.stopDrawing();
    },
    handleCanvasMouseLeave(event) {
      // If icon is being dragged, don't stop drawing
      if (this.isDraggingIcon) return;
      
      this.stopDrawing();
    },
    loadLayoutImage() {
      // console.log('===== LOAD LAYOUT IMAGE STARTED =====');
      
      // Skip if drawing context is not available
      if (!this.drawContext) {
        // console.log('Drawing context not available yet, skipping layout image load');
        // console.log('===== LOAD LAYOUT IMAGE SKIPPED =====');
        return;
      }
      
      if (!this.localLayout) {
        // Clear existing images if the layout image is removed
        // console.log('No localLayout provided, clearing canvas');
        this.drawImage = null;
        this.redrawCanvas();
        // console.log('===== LOAD LAYOUT IMAGE COMPLETED (CLEARED) =====');
        return;
      }
      
      // console.log('Loading layout image with URL:', this.localLayout);
      // console.log('Current layoutSrc:', this.currentLayoutSrc);
      
      this.drawImage = new Image();
      // Enable CORS for the image
      this.drawImage.crossOrigin = 'anonymous';
      
      this.drawImage.onload = () => {
        // console.log('Layout image loaded successfully, dimensions:', this.drawImage.width, 'x', this.drawImage.height);
        if (this.drawContext) {
          this.drawContext.drawImage(this.drawImage, 0, 0, this.width, this.effectiveHeight);
          // console.log('Image drawn on canvas with dimensions:', this.width, 'x', this.effectiveHeight);
          this.redrawSpaces(); // Redraw spaces after loading the image
          // console.log('Spaces redrawn after loading image');
        } else {
          console.error('Drawing context not available when image loaded');
        }
        // console.log('===== LOAD LAYOUT IMAGE COMPLETED =====');
      };
      
      this.drawImage.onerror = (err) => {
        console.error('Error loading layout image for draw canvas:', err);
        console.error('Failed URL:', this.localLayout);
        this.drawImage = null;
        // console.log('===== LOAD LAYOUT IMAGE FAILED =====');
      };
      
      this.drawImage.src = this.localLayout;
      // console.log('Image src set, waiting for load event');
    },
    getCanvasCoordinates(event) {
      if (!this.$refs.drawCanvas) return { x: 0, y: 0 };
      
      const rect = this.$refs.drawCanvas.getBoundingClientRect();
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      };
    },
    getTouchCoordinates(event) {
      if (!event.touches || !event.touches[0]) return null;
      
      const touch = event.touches[0];
      const rect = this.$refs.drawCanvas.getBoundingClientRect();
      return {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      };
    },
    handleTouchStart(event) {
      event.preventDefault(); // Prevent scrolling when drawing
      const coords = this.getTouchCoordinates(event);
      if (coords) {
        this.isDrawing = true;
        // Disable asset interaction while drawing
        this.disableAssetInteraction = true;
        
        // Clear previous path if starting a new one
        if (this.currentPath.length === 0) {
          this.redrawCanvas();
        }
        
        this.currentPath.push(coords);
        
        // Start a new path
        this.drawContext.beginPath();
        this.drawContext.moveTo(coords.x, coords.y);
      }
    },
    handleTouchMove(event) {
      event.preventDefault(); // Prevent scrolling when drawing
      if (!this.isDrawing) return;
      
      const coords = this.getTouchCoordinates(event);
      if (coords) {
        this.currentPath.push(coords);
        
        // Draw on canvas
        this.redrawCanvas();
      }
    },
    handleTouchEnd(event) {
      event.preventDefault();
      this.stopDrawing();
    },
    startDrawing(event) {
      // Start a new path
      this.isDrawing = true;
      this.disableAssetInteraction = true;
      
      // Get mouse position relative to canvas
      const { x, y } = this.getCanvasCoordinates(event);
      
      // Clear previous path if starting a new one
      if (this.currentPath.length === 0) {
        this.redrawCanvas();
      }
      
      // Start a new path
      this.currentPath = [{ x, y }];
      
      // Create a pending space with a UUID
      this.pendingSpace = {
        uuid: new ShortUniqueId().randomUUID(6),
        path: this.currentPath,
        center: { x, y },
        style: {
          lineWidth: this.lineWidth || 2,
          strokeColor: this.getThemeColorWithOpacity('success', 0.25),
          fillColor: this.getThemeColorWithOpacity('success', 0.25)
        }
      };
      
      // Draw the initial point
      this.drawContext.beginPath();
      this.drawContext.moveTo(x, y);
    },
    draw(event) {
      if (!this.isDrawing) return;
      
      // Get mouse position relative to canvas
      const { x, y } = this.getCanvasCoordinates(event);
      
      // Add point to current path
      this.currentPath.push({ x, y });
      
      // Update the pending space path
      if (this.pendingSpace) {
        this.pendingSpace.path = [...this.currentPath];
      }
      
      // Redraw the canvas with the current path
      this.redrawCanvas();
      
      // Draw the current path
      this.drawContext.beginPath();
      this.drawContext.moveTo(this.currentPath[0].x, this.currentPath[0].y);
      
      for (let i = 1; i < this.currentPath.length; i++) {
        this.drawContext.lineTo(this.currentPath[i].x, this.currentPath[i].y);
      }
      
      this.drawContext.strokeStyle = 'success';
      this.drawContext.lineWidth = 2;
      this.drawContext.stroke();
    },
    stopDrawing() {
      if (!this.isDrawing) return;
      
      this.isDrawing = false;
      
      // If we have a valid path (at least 3 points), close it and create a space
      if (this.currentPath.length >= 3) {
        // Close the path by adding the first point again
        this.currentPath.push({ ...this.currentPath[0] });
        
        // Update the pending space
        if (this.pendingSpace) {
          this.pendingSpace.path = [...this.currentPath];
          this.pendingSpace.center = this.calculateCenter(this.currentPath);
          
          // Show dialog to name the space
          this.newSpaceName = `Space ${this.internalSpaces.length + 1}`;
          this.showNameDialog = true;
        }
      } else {
        // Not enough points, reset
        this.currentPath = [];
        this.pendingSpace = null;
      }
      
      // Redraw the canvas
      this.redrawCanvas();
    },
    calculateCenter(path) {
      // Calculate the center point of the path for placing the name
      let sumX = 0;
      let sumY = 0;
      
      for (const point of path) {
        sumX += point.x;
        sumY += point.y;
      }
      
      return {
        x: sumX / path.length,
        y: sumY / path.length
      };
    },
    confirmSpaceName() {
      // Create a new space with the current path
      if (this.pendingSpace && this.pendingSpace.path && this.pendingSpace.path.length > 0) {
        // Calculate the center of the space
        const center = this.calculateCenter(this.pendingSpace.path);
        
        // Create a default style for the space
        const defaultStyle = {
          lineWidth: this.lineWidth || 2,
          strokeColor: this.getThemeColorWithOpacity('success', 0.25),
          fillColor: this.getThemeColorWithOpacity('success', 0.25)
        };
        
        // Create a new space object
        const newSpace = {
          uuid: new ShortUniqueId().randomUUID(6),
          assetName: this.newSpaceName || `Space ${this.internalSpaces.length + 1}`,
          path: this.pendingSpace.path,
          center: center,
          style: defaultStyle
        };
        
        // Add to internal spaces array
        const updatedSpaces = [...this.internalSpaces, newSpace];
        this.internalSpaces = updatedSpaces;
        
        // Emit events
        this.$emit('space-created', newSpace);
        this.$emit('spaces-updated', this.internalSpaces);
        
        // Check if any icons are in this space
        this.checkIconInSpace(newSpace);
        
        // Check if any assets are in this space
        this.checkAssetInNewSpace(newSpace);
        
        // Reset current path and pending space
        this.currentPath = [];
        this.pendingSpace = null;
        this.newSpaceName = '';
        this.showNameDialog = false;
        
        // Re-enable asset interaction after space is created
        this.disableAssetInteraction = false;
        
        // Redraw the canvas
        this.redrawCanvas();
      }
    },
    cancelDrawing() {
      // Reset current path and pending space
      this.currentPath = [];
      this.pendingSpace = null;
      this.showNameDialog = false;
      // Re-enable asset interaction
      this.disableAssetInteraction = false;
      
      // Redraw canvas without the pending space
      this.redrawCanvas();
    },
    redrawCanvas() {
      // console.log('===== REDRAW CANVAS STARTED =====');
      
      // Skip if drawing context is not available
      if (!this.drawContext) {
        // console.log('Drawing context not available, skipping redraw');
        // console.log('===== REDRAW CANVAS SKIPPED =====');
        return;
      }
      
      // console.log('Canvas state before redraw:', {
      //   width: this.width,
      //   height: this.effectiveHeight,
      //   hasDrawImage: this.drawImage ? true : false,
      //   spacesCount: this.allSpaces.length,
      //   hasCurrentPath: this.currentPath.length > 0
      // });
      
      // Clear canvas
      this.drawContext.clearRect(0, 0, this.width, this.effectiveHeight);
      
      // Redraw layout image
      if (this.drawImage) {
        // console.log('Drawing image on canvas');
        this.drawContext.drawImage(this.drawImage, 0, 0, this.width, this.effectiveHeight);
      } else {
        // console.log('No image to draw on canvas');
      }
      
      // Draw all saved spaces with their names
      this.redrawSpaces();
      
      // Draw current path with current style
      if (this.currentPath.length > 0) {
        // console.log('Drawing current path with', this.currentPath.length, 'points');
        this.drawPath(this.currentPath, this.drawContext);
      }
      
      // console.log('===== REDRAW CANVAS COMPLETED =====');
    },
    redrawSpaces() {
      // Draw all saved spaces with their original styles
      this.allSpaces.forEach(space => {
        // Skip spaces without a valid path
        if (!space || !space.path || space.path.length < 2) return;
        
        // Check if this space is being hovered in the list
        const isHighlighted = this.hoveredSpace && this.hoveredSpace.uuid === space.uuid;
        
        // Ensure space.style exists and has valid properties
        const defaultStyle = {
          lineWidth: this.lineWidth || 2,
          strokeColor: this.getThemeColorWithOpacity('success', 0.25),
          fillColor: this.getThemeColorWithOpacity('success', 0.25)
        };
        
        // Create a style object with defaults for missing properties
        const style = {
          lineWidth: (space.style && space.style.lineWidth) || defaultStyle.lineWidth,
          strokeColor: (space.style && space.style.strokeColor) || defaultStyle.strokeColor,
          fillColor: (space.style && space.style.fillColor) || defaultStyle.fillColor
        };
        
        // Apply highlight if needed
        if (isHighlighted) {
          style.lineWidth = style.lineWidth * 2;

          // Parse the existing fill color to extract its components
          const rgbaMatch = style.fillColor.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
          if (rgbaMatch) {
            // Extract all components
            const r = rgbaMatch[1];
            const g = rgbaMatch[2];
            const b = rgbaMatch[3];
            const currentAlpha = parseFloat(rgbaMatch[4]);
            
            // Increase the alpha by 50% (capped at 0.9 for better visibility)
            const newAlpha = Math.min(currentAlpha * 2, 0.5);
            
            style.fillColor = `rgba(${r}, ${g}, ${b}, ${newAlpha})`;
          }
        }
        
        this.drawPathWithStyle(space.path, this.drawContext, style);
        this.drawSpaceName(space.assetName, space.center, this.drawContext, isHighlighted);
      });
    },
    drawPathWithStyle(path, context, style) {
      if (path.length < 2) return;
      
      // Save current context state
      context.save();
      
      // Apply the saved style
      context.lineWidth = style.lineWidth;
      context.strokeStyle = style.strokeColor;
      context.fillStyle = style.fillColor;
      
      // Draw the path
      context.beginPath();
      context.moveTo(path[0].x, path[0].y);
      
      for (let i = 1; i < path.length; i++) {
        context.lineTo(path[i].x, path[i].y);
      }
      
      context.stroke();
      
      // Fill if path is closed (first and last points are the same)
      if (path.length > 2 && 
          path[0].x === path[path.length - 1].x && 
          path[0].y === path[path.length - 1].y) {
        context.fill();
      }
      
      // Restore context state
      context.restore();
    },
    drawSpaceName(name, center, context, isHighlighted = false) {
      if (!name || !center) return;
      
      // Save current context state
      context.save();
      
      // Set text style with larger font if highlighted
      const fontSize = isHighlighted ? 28 : 20;
      context.font = `bold ${fontSize}px Arial`;
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      
      // Add white background for better readability
      const textWidth = context.measureText(name).width;
      context.fillStyle = isHighlighted ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.7)';
      context.fillRect(
        center.x - textWidth/2 - 5, 
        center.y - fontSize/2 - 5, 
        textWidth + 10, 
        fontSize + 10
      );
      
      // Draw text
      context.fillStyle = isHighlighted ? '#1976D2' : '#000';
      context.fillText(name, center.x, center.y);
      
      // Restore context state
      context.restore();
    },
    drawPath(path, context) {
      if (path.length < 2) return;
      
      context.beginPath();
      context.moveTo(path[0].x, path[0].y);
      
      for (let i = 1; i < path.length; i++) {
        context.lineTo(path[i].x, path[i].y);
      }
      
      context.stroke();
      
      // Fill if path is closed (first and last points are the same)
      if (path.length > 2 && 
          path[0].x === path[path.length - 1].x && 
          path[0].y === path[path.length - 1].y) {
        context.fill();
      }
    },
    clearCanvas() {
      // console.log('===== CLEAR CANVAS CALLED =====');
      // console.log('Current state before clearing:', {
      //   hasCoordinates: this.coordinates.length > 0,
      //   hasCurrentPath: this.currentPath.length > 0,
      //   hasInternalSpaces: this.internalSpaces.length > 0,
      //   localLayout: this.localLayout ? 'exists' : 'null',
      //   currentLayoutSrc: this.currentLayoutSrc ? 'exists' : 'null',
      //   drawImage: this.drawImage ? 'exists' : 'null'
      // });
      
      // Clear geometry data
      this.coordinates = [];
      this.currentPath = [];
      this.internalSpaces = [];
      this.iconInSpace = null;
      this.iconInSpaces = [];
      
      // Reset icon position
      this.resetIconPosition();
      
      // Clear layout image (using local data property)
      this.localLayout = null;
      this.drawImage = null;
      this.currentLayoutSrc = null; // Also clear this reference
      
      // console.log('State after clearing:', {
      //   hasCoordinates: this.coordinates.length > 0,
      //   hasCurrentPath: this.currentPath.length > 0,
      //   hasInternalSpaces: this.internalSpaces.length > 0,
      //   localLayout: this.localLayout ? 'exists' : 'null',
      //   currentLayoutSrc: this.currentLayoutSrc ? 'exists' : 'null',
      //   drawImage: this.drawImage ? 'exists' : 'null'
      // });
      
      // Emit events to notify parent
      this.$emit('layout-selected', {
        file: null,
        imageUrl: null,
        dimensions: {
          width: this.width,
          height: this.height
        }
      });
      
      this.$emit('spaces-updated', this.internalSpaces);
      this.$emit('icon-in-space', null);
      this.$emit('icon-in-spaces', []);
      
      // Redraw the canvas (will be empty now)
      this.redrawCanvas();
      // console.log('===== CLEAR CANVAS COMPLETED =====');
    },
    resetIconPosition() {
      // Reset the icon position to a default position
      this.iconPosition = { x: 50, y: 50 };
      this.checkIconInSpaces();
    },
    setIconPosition(x, y) {
      // Set the icon position to the specified coordinates
      this.iconPosition = { 
        x: Math.max(0, Math.min(x, this.width)),
        y: Math.max(0, Math.min(y, this.effectiveHeight))
      };
    },
    checkIconInSpaces() {
      // Check if the icon is inside any space
      const iconPosition = this.iconPosition;
      
      // Reset the array of spaces the icon is in
      this.iconInSpaces = [];
      this.iconInSpace = null;
      
      // Check each space
      this.allSpaces.forEach(space => {
        if (this.isPointInPath(iconPosition, space.path)) {
          this.iconInSpaces.push(space);
          
          // For backward compatibility, set the first space as the iconInSpace
          if (!this.iconInSpace) {
            this.iconInSpace = space;
          }
        }
      });
      
      // Emit events to notify parent
      this.$emit('icon-in-space', this.iconInSpace);
      this.$emit('icon-in-spaces', this.iconInSpaces);
    },
    getIconPosition() {
      // Return a copy of the current icon position
      return { ...this.iconPosition };
    },
    updateCanvasDimensions() {
      // Only proceed if the canvas elements are available
      if (!this.$refs.drawCanvas) {
        // console.log('Draw canvas not available yet, skipping dimension update');
        return;
      }
      
      const drawCanvas = this.$refs.drawCanvas;
      // console.log('Canvas element found, setting dimensions:', this.width, 'x', this.effectiveHeight);
      
      // Set canvas dimensions
      drawCanvas.width = this.width;
      drawCanvas.height = this.effectiveHeight;
      
      // Get draw context
      this.drawContext = drawCanvas.getContext('2d');
      
      // Only update drawing styles if context is available
      if (this.drawContext) {
        // console.log('Drawing context obtained successfully');
        this.updateDrawingStyles();
      } else {
        // console.error('Failed to get drawing context from canvas');
      }
    },
    triggerFileInput(event) {
      // Simply trigger the file input
      // console.log('Triggering file input selection');
      this.$refs.fileInput.click();
    },
    handleFileSelection(event) {
      // console.log('===== FILE SELECTION STARTED =====');
      const file = event.target.files[0];
      // console.log('File selected:', file ? file.name : 'none');
      
      if (file) {
        // Set loading state
        this.isLoading = true;
        
        // Reset the file input value to allow reselecting the same file
        // console.log('Resetting file input value');
        this.$refs.fileInput.value = '';
        
        // Create a URL for the selected file
        const imageUrl = URL.createObjectURL(file);
        // console.log('Created object URL for file:', imageUrl);
        
        // Create a temporary image to get dimensions
        const image = new Image();
        image.onload = () => {
          // console.log('Image loaded successfully with dimensions:', image.width, 'x', image.height);
          
          // Log current state before changes
          // console.log('Current state before loading new layout:', {
          //   hasInternalSpaces: this.internalSpaces.length > 0,
          //   localLayout: this.localLayout ? 'exists' : 'null',
          //   currentLayoutSrc: this.currentLayoutSrc ? 'exists' : 'null',
          //   drawImage: this.drawImage ? 'exists' : 'null'
          // });
          
          // Clear existing spaces when a new layout is selected
          this.internalSpaces = [];
          this.iconInSpace = null;
          this.iconInSpaces = [];
          this.$emit('spaces-updated', this.internalSpaces);
          this.$emit('icon-in-space', null);
          this.$emit('icon-in-spaces', []);
          
          // Reset icon position
          this.resetIconPosition();
          
          // Update local layout (not directly modifying the prop)
          this.localLayout = imageUrl;
          this.currentLayoutSrc = imageUrl; // Set this to track the current layout
          
          // console.log('Updated state after loading new layout:', {
          //   hasInternalSpaces: this.internalSpaces.length > 0,
          //   localLayout: this.localLayout ? 'exists (updated)' : 'null',
          //   currentLayoutSrc: this.currentLayoutSrc ? 'exists (updated)' : 'null'
          // });
          
          // Emit the file and dimensions to parent component
          this.$emit('layout-selected', {
            file: file,
            imageUrl: imageUrl,
            dimensions: {
              width: image.width,
              height: image.height
            }
          });
          // console.log('Emitted layout-selected event with dimensions', image.width, 'x', image.height);
          
          // Load the layout image in this component
          this.loadLayoutImage();
          
          this.isLoading = false;
          // console.log('===== FILE SELECTION COMPLETED =====');
        };
        
        image.onerror = (err) => {
          // console.error('Error loading selected image:', err);
          this.isLoading = false;
          // console.log('===== FILE SELECTION FAILED =====');
        };
        
        // console.log('Setting image source to load');
        image.src = imageUrl;
      } else {
        // console.log('No file selected');
        // console.log('===== FILE SELECTION COMPLETED (NO FILE) =====');
      }
    },
    updateSpaceName(spaceId, newName) {
      // Find the space with the given ID
      const spaceIndex = this.internalSpaces.findIndex(space => space.uuid === spaceId);
      if (spaceIndex !== -1) {
        // Create a copy of the spaces array
        const updatedSpaces = [...this.internalSpaces];
        
        // Update the name in the copied array
        updatedSpaces[spaceIndex] = {
          ...updatedSpaces[spaceIndex],
          assetName: newName
        };
        
        // Update the spaces array
        this.internalSpaces = updatedSpaces;
        
        // Update iconInSpace if needed
        if (this.iconInSpace && this.iconInSpace.uuid === spaceId) {
          this.iconInSpace = this.internalSpaces[spaceIndex];
          this.$emit('icon-in-space', this.iconInSpace);
        }
        
        // Update iconInSpaces if needed
        const iconInSpaceIndex = this.iconInSpaces.findIndex(space => space.uuid === spaceId);
        if (iconInSpaceIndex !== -1) {
          const updatedIconInSpaces = [...this.iconInSpaces];
          updatedIconInSpaces[iconInSpaceIndex] = this.internalSpaces[spaceIndex];
          this.iconInSpaces = updatedIconInSpaces;
          this.$emit('icon-in-spaces', this.iconInSpaces);
        }
        
        // Redraw the canvas to show the updated name
        this.redrawCanvas();
      }
    },
    deleteSpace(spaceId) {
      try {
        // Find the space in the internalSpaces array
        const spaceIndex = this.internalSpaces.findIndex(space => space.uuid === spaceId);
        
        if (spaceIndex !== -1) {
          // Remove from iconInSpaces if it was in this space
          this.iconInSpaces = (this.iconInSpaces || []).filter(space => space.uuid !== spaceId);
          
          // Check if icon is in this space
          if (this.iconInSpace && this.iconInSpace.uuid === spaceId) {
            this.iconInSpace = this.iconInSpaces.length > 0 ? this.iconInSpaces[0] : null;
            this.$emit('icon-in-space', this.iconInSpace);
          }
          
          // Emit updated iconInSpaces
          this.$emit('icon-in-spaces', this.iconInSpaces);
          
          // Remove the space by creating a copy of the array
          const updatedSpaces = [...this.internalSpaces];
          updatedSpaces.splice(spaceIndex, 1);
          this.internalSpaces = updatedSpaces;
        } else {
          // If not found in internalSpaces, check if it's in the spaces prop
          const propSpaceIndex = this.spaces.findIndex(space => space.uuid === spaceId);
          
          if (propSpaceIndex !== -1) {
            // Remove from iconInSpaces if it was in this space
            this.iconInSpaces = (this.iconInSpaces || []).filter(space => space.uuid !== spaceId);
            
            // Check if icon is in this space
            if (this.iconInSpace && this.iconInSpace.uuid === spaceId) {
              this.iconInSpace = this.iconInSpaces.length > 0 ? this.iconInSpaces[0] : null;
              this.$emit('icon-in-space', this.iconInSpace);
            }
            
            // Emit updated iconInSpaces
            this.$emit('icon-in-spaces', this.iconInSpaces);
          }
        }
        
        // Redraw the canvas without the deleted space
        this.redrawCanvas();
      } catch (error) {
        console.error('Error in SpaceEditor.deleteSpace:', error);
        console.error('Error stack:', error.stack);
      }
    },
    getPlacedAssetStyle(asset) {
      // If this is the asset being dragged, use the temporary position
      if (this.isDraggingPlacedAsset && 
          this.currentDraggedAsset && 
          this.currentDraggedAsset.uuid === asset.uuid &&
          this.tempDragPosition) {
        return {
          left: `${this.tempDragPosition.x}px`,
          top: `${this.tempDragPosition.y}px`
        };
      }
      
      // Otherwise use the asset's actual position
      if (!asset.position) return {};
      
      return {
        left: `${asset.position.x}px`,
        top: `${asset.position.y}px`
      };
    },
    isAssetHighlighted(asset) {
      return this.hoveredAsset && this.hoveredAsset.uuid === asset.uuid;
    },
    // Methods for dragging placed assets
    startDraggingPlacedAsset(event, asset) {
      event.preventDefault();
      event.stopPropagation();
      
      // Get initial mouse position relative to the canvas
      const rect = this.$refs.drawCanvas.getBoundingClientRect();
      const initialX = event.clientX || (event.touches && event.touches[0] ? event.touches[0].clientX : 0);
      const initialY = event.clientY || (event.touches && event.touches[0] ? event.touches[0].clientY : 0);
      
      this.isDraggingPlacedAsset = true;
      this.currentDraggedAsset = asset;
      this.tempDragPosition = asset.position ? { ...asset.position } : { x: 50, y: 50 };
      
      // Emit event to notify parent
      this.$emit('asset-drag-start', asset);
    },
    
    updateTempDragPosition(x, y) {
      // Skip bounds checking for smoother dragging
      this.tempDragPosition = { x, y };
    },
    
    finalizeAssetPosition(asset, x, y) {
      // Keep asset within canvas bounds
      const boundedX = Math.max(0, Math.min(this.width, x));
      const boundedY = Math.max(0, Math.min(this.effectiveHeight, y));
      
      // Update asset position
      const updatedAsset = {
        ...asset,
        position: { x: boundedX, y: boundedY }
      };
      
      // Check which spaces the asset is in
      const spaces = [];
      this.spaces.forEach(space => {
        if (this.isPointInPath({ x: boundedX, y: boundedY }, space.path)) {
          spaces.push(space);
        }
      });
      
      // Emit event to update parent
      this.$emit('asset-position-updated', {
        asset: updatedAsset,
        spaces: spaces
      });
    },
    
    updatePlacedAssetPosition(asset, x, y) {
      // This method is kept for backward compatibility
      this.finalizeAssetPosition(asset, x, y);
    },
    
    // New method to check if placed assets are in a new space
    checkAssetInNewSpace(space) {
      if (!space || !this.placedAssets || this.placedAssets.length === 0) return;
      
      // Find all assets that are inside this space
      const assetsInSpace = [];
      
      this.placedAssets.forEach(asset => {
        if (asset.position && this.isPointInPath(asset.position, space.path)) {
          assetsInSpace.push(asset);
        }
      });
      
      // If any assets are in the space, emit an event
      if (assetsInSpace.length > 0) {
        this.$emit('asset-in-new-space', {
          space: space,
          assets: assetsInSpace
        });
      }
    },
  }
}
</script>

<style scoped>
.space-editor {
  margin: 20px 0;
  position: relative;
  max-width: 100%;
}

.hidden-input {
  display: none;
}

.controls-top {
  display: flex;
  justify-content: center;
  margin-bottom: 15px;
  gap: 10px;
}

.canvas-container {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: center;
  max-width: 100%;
}

.canvas-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 100%;
  height: auto;
}

.canvas-area {
  position: relative;
  margin-bottom: 10px;
  max-width: 100%;
  height: auto;
}

.draw-canvas {
  border: 1px solid #ccc;
  background-color: #f8f8f8;
  touch-action: none; /* Prevents default touch actions like scrolling */
  max-width: 100%;
  display: block;
}

.draggable-icon {
  position: absolute;
  z-index: 10;
  cursor: move;
  transform: translate(-50%, -100%); /* Center the bottom of the icon at the point */
  user-select: none;
  touch-action: none;
}

.file-selection-prompt {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(245, 245, 245, 0.9);
  border: 2px dashed #2196F3;
  border-radius: 4px;
  cursor: pointer;
  z-index: 1;
}

.file-selection-prompt p {
  margin-top: 10px;
  color: #2196F3;
  font-weight: 500;
}

h3 {
  margin-bottom: 10px;
  color: #2c3e50;
}

.hidden {
  display: none !important;
}

.placed-asset-icon {
  position: absolute;
  z-index: 9;
  transform: translate(-50%, -100%); /* Center the bottom of the icon at the point */
  user-select: none;
  /* pointer-events is now controlled by the assetIconStyle computed property */
  cursor: move;
  transition: all 0.3s ease;
}

/* Remove transition during drag for immediate response */
.placed-asset-icon.dragging {
  transition: none !important;
}

.placed-asset-icon.highlighted {
  z-index: 11; /* Bring highlighted assets to front */
  filter: drop-shadow(0 0 5px rgba(0,0,0,0.5));
}

.asset-label {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(255, 255, 255, 0.7);
  padding: 2px 5px;
  border-radius: 3px;
  font-size: 20px;
  white-space: nowrap;
  color: #333;
  transition: all 0.3s ease;
}

.highlighted .asset-label {
  background-color: rgba(255, 255, 255, 0.9);
  font-weight: bold;
  font-size: 24px;
  padding: 3px 8px;
}
</style> 